import {
    CitationInfo,
    FileSearchToolCallResponseItemDto,
    OutputTextContentPartDto,
    ResponsesMessageRecord
} from './responses-types.ts';

const FILENAME_CITATION_REGEX = /【[^】]*?†([^】]*)】/g;

// OpenAI Responses API private-use citation markers (e.g. gpt-4o):
//   fileciteturn0file7
// U+E200 START · U+E202 DELIMITER · U+E201 STOP.
// The payload after the first delimiter is a source ref like "turn0file7"
// (optionally followed by another delimiter + line locator, e.g. "…L8-L13").
// turnN = Nth file_search call in the run; fileM = index into THAT call's raw,
// un-deduped results array. See buildTurnFileIdMap.
const CITATION_START = '\uE200';
const OAI_CITATION_REGEX = /\uE200[^\uE202]*\uE202([\s\S]*?)\uE201/g;
const SOURCE_REF_REGEX = /turn(\d+)file(\d+)/;

/**
 * Maps each assistant message id to its run's ordered file_search results:
 * a list of turns, where turn[N] is the ordered list of file ids returned by
 * the Nth file_search call in that run (raw order, duplicates kept — fileM
 * indexes into this directly).
 *
 * A run starts at each user message, so we reset on user messages and snapshot
 * the accumulated turns when an assistant message completes it.
 */
export const buildTurnFileIdMap = (
    messages: ResponsesMessageRecord[]
): Record<string, string[][]> => {
    const map: Record<string, string[][]> = {};
    let currentTurns: string[][] = [];

    for (const record of messages) {
        if (record.messageType === 'user_message') {
            currentTurns = [];
            continue;
        }

        const item = record.message;
        if (item && item.type === 'file_search_call') {
            const results =
                (item as FileSearchToolCallResponseItemDto).results ?? [];
            currentTurns.push(results.map((r) => r.file_id));
            continue;
        }

        if (record.messageType === 'assistant_message') {
            map[record.id] = [...currentTurns];
        }
    }

    return map;
};

/**
 * Replaces OpenAI private-use citation markers with markdown citation links.
 * Each marker's turnN/fileM is resolved to a file id via the run's turn map,
 * then to a citation. Unresolvable markers are stripped so no raw marker text
 * (the garbled "fileciteturn0file7") leaks into the rendered answer.
 */
const replaceOaiMarkers = (
    text: string,
    citations: CitationInfo[],
    turns: string[][]
): string =>
    text.replace(OAI_CITATION_REGEX, (_match, payload: string) => {
        const ref = SOURCE_REF_REGEX.exec(payload);
        if (!ref) return '';

        const fileId = turns[Number(ref[1])]?.[Number(ref[2])];
        if (!fileId) return '';

        const citation = citations.find((c) => c.fileId === fileId);
        return citation ? `[${citation.title}](#citation-${citation.fileId})` : '';
    });

/**
 * Replaces 【†fileId】 placeholders in raw text with markdown citation links.
 * Used as a fallback when the content part has no structured annotations.
 */
const replaceFallback = (text: string, citations: CitationInfo[]): string =>
    text.replaceAll(FILENAME_CITATION_REGEX, (_match, capturedContent) => {
        const potentialIds = capturedContent.split('†');
        const citation = citations.find((c) =>
            potentialIds.some((id: string) => {
                const clean = id.trim();
                return (
                    c.fileId === clean ||
                    c.fileName === clean ||
                    c.fileName === clean + '.txt'
                );
            })
        );
        return citation ? `[${citation.title}](#citation-${citation.fileId})` : '';
    });

/**
 * Builds a renderable markdown string from an output_text content part.
 *
 * Three mutually-exclusive citation formats are handled, in priority order:
 *   1. OpenAI private-use markers (fileciteturnNfileM) —
 *      resolved positionally via the message's turn map.
 *   2. Structured file_citation annotations carrying character positions —
 *      spliced in descending order so later inserts don't shift earlier indices.
 *   3. 【†fileId】 placeholders — regex fallback.
 */
const replaceCitationPlaceholders = (
    part: OutputTextContentPartDto,
    citations: CitationInfo[],
    turns: string[][] = []
): string => {
    const { text, annotations } = part;

    if (text.includes(CITATION_START)) {
        return replaceOaiMarkers(text, citations, turns);
    }

    if (!annotations || annotations.length === 0) {
        return replaceFallback(text, citations);
    }

    const fileCitations = annotations
        .filter((a): a is import('./responses-types.ts').FileCitationAnnotationDto =>
            a.type === 'file_citation'
        )
        .filter((a) => citations.some((c) => c.fileId === a.file_id));

    if (fileCitations.length === 0) {
        return replaceFallback(text, citations);
    }

    // Sort descending by index so each splice doesn't shift subsequent positions.
    const sorted = [...fileCitations].sort((a, b) => b.index - a.index);

    let result = text;
    for (const ann of sorted) {
        const citation = citations.find((c) => c.fileId === ann.file_id);
        if (!citation) continue;
        const link = ` [${citation.title}](#citation-${citation.fileId})`;
        result = result.slice(0, ann.index) + link + result.slice(ann.index);
    }

    return result;
};

export default replaceCitationPlaceholders;
